/**
 * optimisticWriteGuard — the shared primitive for defending an optimistic
 * client write against a stale read-replica refetch (LEV-1319).
 *
 * The bug class: the client writes optimistically (a card moves, a block
 * flips to APPROVED), the mutation commits on the primary, and the event
 * poller's follow-up refetch is answered by a replica that hasn't caught up.
 * Whatever hydrates or re-renders from that refetch overwrites the newer
 * optimistic value with the older server one — the "snapback" / DRAFT↔APPROVED
 * flap. Three trackers grew independently around this (LEV-891/991 pipelines,
 * LEV-849 tasks, LEV-1310 approvals); this module is the one implementation
 * they now share, in two modes because the two data paths have different
 * chokepoints:
 *
 * - `createDeferReconcileGuard` — for data read straight from the React Query
 *   cache (pipelines, tasks). The cache IS the state, so the only defence is
 *   to hold the poller's reconcile while a write is in flight and flush it
 *   once after a cool-down. Cheap: the deferred query has a single consumer.
 * - `createValuePinGuard` — for data hydrated from the project snapshot
 *   (document approval). The snapshot feeds the whole canvas, so its refetch
 *   cannot be held; instead hydration consults the pin and refuses to move the
 *   one field backwards until a snapshot confirms it or the window elapses.
 *
 * Each call returns an independent instance holding its own plain module
 * state (never rendered — read from a mutation lifecycle and a polling hook
 * or the hydration path). Instantiate ONCE per domain in a small tracker file
 * (see `pipelineMoveTracker.ts`, `taskMoveTracker.ts`,
 * `documentApprovalTracker.ts`); the next optimistic surface picks a mode
 * here instead of minting a fourth tracker.
 */

// ---------------------------------------------------------------------------
// Mode 1: defer-reconcile
// ---------------------------------------------------------------------------

export interface DeferReconcileGuard {
  /** Cool-down (ms) between the last write settling and the flush. */
  readonly delayMs: number;
  /** Mark an optimistic write as in flight (mutation onMutate). */
  begin(): void;
  /**
   * Mark a write as settled (mutation onSettled — success OR error). When the
   * last in-flight write settles, ALWAYS opens the cool-down window — even if
   * no reconcile is parked yet — so a poll landing right after settle defers
   * instead of refetching a replica that hasn't caught up.
   */
  end(): void;
  /**
   * True while any write awaits its server response OR the post-settle
   * cool-down is still running. Both windows are part of the guard.
   */
  hasPending(): boolean;
  /**
   * Park (or replace) the reconcile to run once the cool-down elapses. Single
   * slot: invalidates are idempotent, so the latest callback covers every
   * poll that was deferred.
   */
  defer(reconcile: () => void): void;
  /** True while a reconcile is parked and has not yet flushed. */
  hasParked(): boolean;
  /** Test-only: clear counter, parked reconcile, and timer. */
  reset(): void;
}

export function createDeferReconcileGuard(opts: {
  delayMs: number;
}): DeferReconcileGuard {
  const { delayMs } = opts;
  let pending = 0;
  let parked: (() => void) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancelTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    delayMs,
    begin() {
      pending++;
      // A new write just started — the flush must wait until THIS one
      // settles too. The parked reconcile survives the cancel.
      cancelTimer();
    },
    end() {
      pending = Math.max(0, pending - 1);
      if (pending === 0) {
        cancelTimer();
        timer = setTimeout(() => {
          timer = null;
          const cb = parked;
          parked = null;
          cb?.();
        }, delayMs);
      }
    },
    hasPending() {
      return pending > 0 || timer !== null;
    },
    defer(reconcile) {
      parked = reconcile;
    },
    hasParked() {
      return parked !== null;
    },
    reset() {
      pending = 0;
      parked = null;
      cancelTimer();
    },
  };
}

// ---------------------------------------------------------------------------
// Mode 2: value-pinning
// ---------------------------------------------------------------------------

export interface ValuePinGuard<K = string> {
  /** How long (ms) a pin defends the optimistic value before giving up. */
  readonly windowMs: number;
  /** Record an optimistic write for `key` at clock time `now`. */
  mark(key: K, now: number): void;
  /**
   * True while the optimistic value for `key` should still win over a
   * server read. Expired pins are pruned on read (this key) and on every
   * `mark` (all keys), so the map is bounded by the window, not the session.
   */
  isPending(key: K, now: number): boolean;
  /** Drop the pin — call once a server read confirms the optimistic value. */
  clear(key: K): void;
  /** Test-only: drop every pin. */
  reset(): void;
}

export function createValuePinGuard<K = string>(opts: {
  windowMs: number;
}): ValuePinGuard<K> {
  const { windowMs } = opts;
  const pins = new Map<K, number>();

  return {
    windowMs,
    mark(key, now) {
      // Sweep every expired pin on each mark so a key that is never read
      // again (block deleted, user navigated away before the next snapshot)
      // cannot accumulate for the life of the page. Marks are rare (one per
      // user action) and the map holds at most the pins from the last
      // `windowMs`, so the sweep is O(handful).
      for (const [k, markedAt] of pins) {
        if (now - markedAt > windowMs) pins.delete(k);
      }
      pins.set(key, now);
    },
    isPending(key, now) {
      const markedAt = pins.get(key);
      if (markedAt === undefined) return false;
      if (now - markedAt > windowMs) {
        pins.delete(key);
        return false;
      }
      return true;
    },
    clear(key) {
      pins.delete(key);
    },
    reset() {
      pins.clear();
    },
  };
}
